import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';
import { useNotification } from '../../context/NotificationContext';

const initialFormState = {
  date: '',
  provider_code: '',
  channel: '',
  currency: '',
  amount: '',
  type: '',
  sim_rate: '',
  sim_used: '',
  code: '',
  name: '',
  channels: '',
  currencies: '',
};

const FIELD_CONFIG = {
  earnings: ['date', 'provider_code', 'channel', 'currency', 'amount'],
  expenses: ['date', 'type', 'amount', 'sim_rate', 'sim_used'],
  providers: ['code', 'name', 'channels', 'currencies'],
  withdrawn: ['date', 'provider_code', 'channel', 'currency', 'amount'],
};

function normalizeDate(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const direct = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (direct) return direct[1];
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildPayload(collection, form) {
  if (collection === 'providers') {
    return {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      channels: form.channels.split(',').map((v) => v.trim().toUpperCase()).filter(Boolean),
      currencies: form.currencies.split(',').map((v) => v.trim().toUpperCase()).filter(Boolean),
    };
  }

  if (collection === 'expenses') {
    const payload = {
      date: form.date,
      type: form.type.trim().toLowerCase(),
      amount: form.amount === '' ? null : Number(form.amount),
      sim_rate: form.sim_rate === '' ? null : Number(form.sim_rate),
      sim_used: form.sim_used === '' ? null : Number(form.sim_used),
    };
    if (payload.type === 'load') {
      payload.sim_rate = null;
      payload.sim_used = null;
    }
    return payload;
  }

  return {
    date: form.date,
    provider_code: form.provider_code.trim().toUpperCase(),
    channel: form.channel.trim().toUpperCase(),
    currency: form.currency.trim().toUpperCase(),
    amount: form.amount === '' ? null : Number(form.amount),
  };
}

const AddSmsVoipRecordDialog = ({
  open,
  onClose,
  onSuccess,
  apiBase,
  collection,
  recordToEdit = null,
  providers = [],
  defaultSimRate = 25,
}) => {
  const { showNotification } = useNotification();
  const [form, setForm] = useState(initialFormState);
  const fields = useMemo(() => FIELD_CONFIG[collection], [collection]);
  const isEditing = Boolean(recordToEdit);

  useEffect(() => {
    if (!open) return;
    if (recordToEdit) {
      setForm({
        ...initialFormState,
        date: normalizeDate(recordToEdit.date),
        provider_code: recordToEdit.provider_code || '',
        channel: recordToEdit.channel || '',
        currency: recordToEdit.currency || '',
        amount: recordToEdit.amount ?? '',
        type: recordToEdit.type || '',
        sim_rate: recordToEdit.sim_rate ?? '',
        sim_used: recordToEdit.sim_used ?? '',
        code: recordToEdit.code || '',
        name: recordToEdit.name || '',
        channels: Array.isArray(recordToEdit.channels) ? recordToEdit.channels.join(', ') : '',
        currencies: Array.isArray(recordToEdit.currencies) ? recordToEdit.currencies.join(', ') : '',
      });
    } else {
      const defaults = { ...initialFormState };
      if (collection === 'expenses') {
        defaults.type = 'sim';
        defaults.sim_rate = defaultSimRate;
      }
      setForm(defaults);
    }
  }, [open, recordToEdit, collection, defaultSimRate]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if ((collection === 'earnings' || collection === 'withdrawn') && field === 'channel') {
        const channel = value.toUpperCase();
        if (channel === 'SMS') next.currency = 'EUR';
        if (channel === 'VOIP') next.currency = 'USD';
      }
      if (collection === 'expenses' && field === 'type') {
        if (value === 'load') {
          next.sim_rate = '';
          next.sim_used = '';
        } else if (value === 'sim') {
          next.sim_rate = defaultSimRate;
          if (next.sim_used !== '' && !Number.isNaN(Number(next.sim_used))) {
            next.amount = Number(next.sim_used) * Number(defaultSimRate);
          } else {
            next.amount = '';
          }
        }
      }
      if (collection === 'expenses' && next.type === 'sim') {
        const simUsed = Number(next.sim_used);
        const simRate = Number(next.sim_rate);
        if (!Number.isNaN(simUsed) && !Number.isNaN(simRate) && next.sim_used !== '') {
          next.amount = simUsed * simRate;
        } else {
          next.amount = '';
        }
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    try {
      if ((collection === 'earnings' || collection === 'withdrawn') && !form.provider_code) {
        showNotification('Please select a provider.', 'warning');
        return;
      }
      if ((collection === 'earnings' || collection === 'withdrawn') && !form.channel) {
        showNotification('Please select a channel.', 'warning');
        return;
      }
      if (collection === 'expenses' && form.type === 'sim' && form.sim_used === '') {
        showNotification('Please enter SIM used for SIM expense.', 'warning');
        return;
      }
      const payload = buildPayload(collection, form);
      const url = isEditing
        ? `${apiBase}/${collection}/${recordToEdit._id}`
        : `${apiBase}/${collection}`;
      const method = isEditing ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      showNotification(isEditing ? 'Record updated successfully!' : 'Record added successfully!', 'success');
      onSuccess();
    } catch (error) {
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>{isEditing ? `Edit ${collection}` : `Add ${collection}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ToggleButtonGroup
            exclusive
            value={collection}
            disabled
            sx={{ alignSelf: 'center' }}
          >
            <ToggleButton value={collection}>{collection.toUpperCase()}</ToggleButton>
          </ToggleButtonGroup>

          {fields.includes('date') && (
            <TextField
              type="date"
              size="small"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => handleChange('date', e.target.value)}
              required
            />
          )}

          {fields.includes('provider_code') && (
            <FormControl fullWidth size="small">
              <InputLabel>Provider Code</InputLabel>
              <Select
                value={form.provider_code}
                label="Provider Code"
                onChange={(e) => handleChange('provider_code', e.target.value)}
              >
                {providers.map((provider) => (
                  <MenuItem key={provider.code} value={provider.code}>
                    {provider.code} - {provider.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {fields.includes('channel') && (
            <FormControl fullWidth size="small">
              <InputLabel>Channel</InputLabel>
              <Select value={form.channel} label="Channel" onChange={(e) => handleChange('channel', e.target.value)}>
                <MenuItem value="SMS">SMS</MenuItem>
                <MenuItem value="VOIP">VOIP</MenuItem>
              </Select>
            </FormControl>
          )}

          {fields.includes('currency') && (
            <FormControl fullWidth size="small">
              <InputLabel>Currency</InputLabel>
              <Select
                value={form.currency}
                label="Currency"
                onChange={(e) => handleChange('currency', e.target.value)}
                disabled={collection === 'earnings' || collection === 'withdrawn'}
              >
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
          )}

          {fields.includes('amount') && (
            <TextField
              size="small"
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              required
              disabled={collection === 'expenses' && form.type === 'sim'}
            />
          )}

          {fields.includes('type') && (
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={form.type} label="Type" onChange={(e) => handleChange('type', e.target.value)}>
                <MenuItem value="sim">sim</MenuItem>
                <MenuItem value="load">load</MenuItem>
              </Select>
            </FormControl>
          )}

          {fields.includes('sim_rate') && (
            <TextField
              size="small"
              label="Sim Rate"
              type="number"
              value={form.sim_rate}
              disabled={form.type === 'load'}
              onChange={(e) => handleChange('sim_rate', e.target.value)}
            />
          )}

          {fields.includes('sim_used') && (
            <TextField
              size="small"
              label="Sim Used"
              type="number"
              value={form.sim_used}
              disabled={form.type === 'load'}
              onChange={(e) => handleChange('sim_used', e.target.value)}
            />
          )}

          {fields.includes('code') && (
            <TextField
              size="small"
              label="Code"
              value={form.code}
              onChange={(e) => handleChange('code', e.target.value)}
              required
            />
          )}

          {fields.includes('name') && (
            <TextField
              size="small"
              label="Name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          )}

          {fields.includes('channels') && (
            <TextField
              size="small"
              label="Channels (comma separated)"
              value={form.channels}
              onChange={(e) => handleChange('channels', e.target.value)}
              required
            />
          )}

          {fields.includes('currencies') && (
            <TextField
              size="small"
              label="Currencies (comma separated)"
              value={form.currencies}
              onChange={(e) => handleChange('currencies', e.target.value)}
              required
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {isEditing ? 'Save Changes' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSmsVoipRecordDialog;
